import React, { useMemo } from 'react';
import { Autocomplete, Chip, TextField, createFilterOptions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PropertyDefinitionBelongsTo, PropertyDefinitionHas } from '../types';
import type { RelationValue } from './EntryPropertyBelongsToEditDialog';
import { RelationOption, useRelationOptions } from '../hooks/useRelationOptions';

const CREATE_NEW_OPTION: RelationOption = { id: '__create-new__', label: 'Create new' };
const filterRelationOptions = createFilterOptions<RelationOption>();

export type RelationSelectFieldProps = {
	def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	values: RelationValue[];
	disabled: boolean;
	autoFocus: boolean;
	onChange: (nextValues: RelationValue[]) => void;
	onCreateNewClick: (currentEntryIds: string[]) => void;
};

export function RelationSelectField({ def, values, disabled, autoFocus, onChange, onCreateNewClick }: RelationSelectFieldProps) {
	const selectedEntryIds = useMemo(() => values.map(({ value }) => value), [values]);
	const {
		options,
		isLoading,
		allowMany,
		disableSelect,
		isEntryArchived,
		canDeselectEntry,
		labelForEntryId,
	} = useRelationOptions({ def, values, selectedEntryIds, enabled: true });

	const selectedOptions = useMemo(
		() => selectedEntryIds.map((id) => options.find((option) => option.id === id) ?? { id, label: labelForEntryId(id) }),
		[selectedEntryIds, options, labelForEntryId],
	);
	const optionsWithSelected = useMemo(
		() => [...options, ...selectedOptions.filter((selected) => !options.some((option) => option.id === selected.id))],
		[options, selectedOptions],
	);

	const toRelationValues = (relationOptions: RelationOption[]) => relationOptions.map(({ id, label }) => ({ value: id, label }));

	const handleChange = (next: RelationOption | RelationOption[] | null) => {
		const nextOptions = Array.isArray(next) ? next : next ? [next] : [];
		if (nextOptions.some(({ id }) => id === CREATE_NEW_OPTION.id)) {
			onCreateNewClick(selectedEntryIds);
			return;
		}
		if (!allowMany) {
			onChange(toRelationValues(nextOptions));
			return;
		}
		const lockedOptions = selectedOptions.filter(
			(selected) => !canDeselectEntry(selected.id) && !nextOptions.some((option) => option.id === selected.id),
		);
		onChange(toRelationValues([...lockedOptions, ...nextOptions]));
	};

	return (
		<Autocomplete<RelationOption, boolean, boolean>
			fullWidth
			multiple={allowMany}
			options={optionsWithSelected}
			value={allowMany ? selectedOptions : selectedOptions[0] ?? null}
			onChange={(_event, next) => handleChange(next)}
			getOptionLabel={(option) => option.label}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			filterSelectedOptions={allowMany}
			filterOptions={(relationOptions, state) => [
				...(disableSelect ? [] : filterRelationOptions(relationOptions, state)),
				CREATE_NEW_OPTION,
			]}
			renderOption={(optionProps, option) => {
				const { key, ...liProps } = optionProps;
				return (
					<li key={key} {...liProps}>
						{option.id === CREATE_NEW_OPTION.id ? (
							<>
								<AddIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
								{option.label}
							</>
						) : option.label}
					</li>
				);
			}}
			disableClearable={!allowMany && Boolean(selectedOptions[0]) && !canDeselectEntry(selectedOptions[0].id)}
			disabled={disabled}
			loading={isLoading}
			renderTags={(tagOptions, getTagProps) => tagOptions.map((option, index) => {
				const { key, onDelete, ...tagProps } = getTagProps({ index });
				return (
					<Chip
						key={key}
						{...tagProps}
						size="small"
						label={option.label}
						onDelete={canDeselectEntry(option.id) ? onDelete : undefined}
						sx={{ textDecoration: isEntryArchived(option.id) ? 'line-through' : 'none' }}
					/>
				);
			})}
			renderInput={(params) => <TextField {...params} variant="filled" margin="dense" label={def.label} autoFocus={autoFocus} />}
		/>
	);
}
